import { Quasar, Dark, type QuasarPluginOptions, Notify } from "quasar";
import { createApp, type App as VueApp } from "vue";
import { HugeiconsPlugin } from "@hugeicons/vue";
import Icon from "@/assets/Icon.vue";
import { mapQuasarIconName } from "@/icons/quasar-icon-map";

import App from "@/App.vue";
import router from "@/router";
import { isAnonymousAccess } from "@/router/route-access";
import store from "../root/store/store";
import { constants } from "@/common/constant/constants";
import { initHttpService } from "@/common/helper/http-service/http-service";
import { Enums } from "@/common/helper/enums";
import CustomEventEmitter from "@/common/helper/event-emitter";
import { UrlHelper } from "@/common/helper/url-helper-service";
import { authService } from "@/common/services/auth-service";
import { isPortalGoreloIoHost } from "@/common/helper/public-function-access";
import { bootstrapRegionFromKv } from "@/common/services/region-lookup-service";
import EnvironmentVariables from "@/EnvironmentVariables";
import { ErrorTemplateBuilder } from "@/infrastructure/error-template-builder";
import {
  installAppInsightsForVue3,
  setAppInsightsStore,
} from "@/root/AppInsights";
import PresetLoader from "@/root/helper/preset-loader";
import type { ShowSnackbarPayload } from "@/root/store/modules/master-state";
import PrototypeInstance from "@/common/helper/prototype-instance";

let appInstance: VueApp | null = null;

/**
 * Gorelo `main.ts` init sequence adapted for Vue 3:
 * no `new Vue`, no `Vue.use`, no `Vue.prototype` — plugins use `app.use` / `globalProperties`.
 * App is created and mounted from `PresetLoader.jobCompleted` (after preset / theme).
 */
export async function init(): Promise<void> {
  setAppInsightsStore(store);

  window.SetIsDesktopApp = async function (isDesktopApp: string) {
    localStorage.setItem("IsDesktopApp", isDesktopApp);
    await authService.signIn();
  };

  await bootstrapRegionFromKv();

  // Skip MSAL init on magic-link verify — auth type is set after token verification.
  if (!UrlHelper.isPublicURL() && !UrlHelper.isAuthVerificationURL()) {
    await authService.init();
  }

  // portal.gorelo.io: contact email is often only available after MSAL redirect / magic-link init.
  if (isPortalGoreloIoHost()) {
    await bootstrapRegionFromKv();
  }

  EnvironmentVariables.SetWindowVariables();

  const presetLoader = new PresetLoader();
  initHttpService(store, undefined);

  const shellGlobalProperties =
    PrototypeInstance.getInstance().config.globalProperties;
  shellGlobalProperties.$store = store;
  shellGlobalProperties.$storeInstance = store;

  presetLoader.jobCompleted = (themeId: number) => {
    Dark.set(themeId === 1);

    const app = createApp(App);

    const quasarOptions: Partial<QuasarPluginOptions> = {
      plugins: {
        Notify,
      },
      config: {
        dark: true,
        // Quasar applies this at runtime (see IconSet.js); typings omit iconMapFn on QuasarUIConfiguration.
      },
    };
    (quasarOptions.config as Record<string, unknown>).iconMapFn = (
      iconName: string,
    ) => {
      const mappedIcon = mapQuasarIconName(iconName);
      return mappedIcon ? { icon: mappedIcon } : void 0;
    };
    app.use(Quasar, quasarOptions);
    app.use(HugeiconsPlugin);
    app.use(router);
    app.use(store);

    app.component("Icon", Icon);

    installAppInsightsForVue3(app, {
      instrumentationKey: constants.appInsightsKey,
    });

    const assignSharedGlobals = (gp: typeof shellGlobalProperties) => {
      gp.$store = store;
      /** Legacy MSP name; same store instance (for `this.$storeInstance` in options components). */
      gp.$storeInstance = store;
      /** Legacy MSP name; Vue Router 4 instance (for `this.$routeInstance` / programmatic navigation). */
      gp.$routeInstance = router;
      gp.title = "abc";

      gp.showSnackbar = (
        message: string,
        isSuccess = true,
        Timeout = 3000,
        Icon: Enums.SnackbarIcon = Enums.SnackbarIcon.None,
      ) => {
        const payload: ShowSnackbarPayload = {
          isSuccess,
          message,
          Timeout,
          Icon,
        };
        void store.dispatch("MasterModule/ShowSnackbar", payload);
      };
    };

    assignSharedGlobals(shellGlobalProperties);
    assignSharedGlobals(app.config.globalProperties);

    app.config.globalProperties.setSelectedLeftMenuItem = (
      itemName: string,
    ) => {
      document.getElementById(itemName)?.click();
    };

    void (async () => {
      await router.isReady();
      const current = router.currentRoute.value;
      const isAuth = authService.isAuthenticated();
      if (isAuth) {
        // Login is `public` (anonymous-access) but must not flash for signed-in users.
        if (current.name === "Login") {
          await router.replace({ name: "TicketCreateComponent" });
        }
      } else if (!isAnonymousAccess(current)) {
        await router.replace({ name: "Login" });
      }
      app.mount("#app");
    })().catch((e) => {
      console.error("[AzureAD Auth] legacy-init — error in router-ready handler:", e);
      app.mount("#app");
    });
  };

  CustomEventEmitter.getInstance().on("bootApp", (themeId: number) => {
    if (appInstance && themeId) {
      Dark.set(themeId === 1);
    }
  });

  try {
    const isLoginBootstrap =
      !authService.isAuthenticated() &&
      !UrlHelper.isPublicURL() &&
      !UrlHelper.isAuthVerificationURL();

    if (isLoginBootstrap) {
      store.commit("MasterModule/SET_LOGIN_BRANDING_LOADING", true);
      await presetLoader.initUnauthenticated(store);
      presetLoader.jobCompleted(1);
    } else {
      await presetLoader.Init(store);
    }
  } catch (e) {
    console.error(ErrorTemplateBuilder.getCatchText("PresetLoader.Init", e));
    store.commit("MasterModule/SET_LOGIN_BRANDING_LOADING", false);
  }
}
